// Bezetting.js: het tabblad Bezetting
//
// Toont hoe druk de bestaande laadpunten van een gemeente gebruikt worden,
// gemeten elk uur uit de status die de exploitanten doorgeven (AFIR-gegevens
// van Eco-Movement en Road via transportdata.be). De cijfers rekent de server
// uit; dit scherm toont ze. Het aantal bij te plaatsen laadpunten uit het plan
// staat hier bewust niet: dat hangt af van keuzes die in de workshop op andere
// tabbladen worden gemaakt (HL, 14 oktober 2026).
//
// Alleen zichtbaar voor de rol beheerder, tenzij de instelling
// BEZETTING_VOOR_IEDEREEN op de server op 1 staat. De server weigert de cijfers
// aan iedereen anders, ook als dit scherm toch zou worden opgeroepen.
//
// Props:
//   actief         staat het tabblad in beeld
//   gemeente       de gekozen gemeente, met wijken (id, naam, lat, lng)
//   wijkResults    de wijken die in de berekening meetellen
//   existingPalen  de laadpunten uit de MOW-database
//   sectorenGeo    de sectorvormen met WIJK_ID per sector

import React, { useState, useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getBezetting, bewaarBezettingDrempel, downloadBezettingExcel } from './api';

// Zelfde adres en sleutel als de andere kaarten, zie KAART_ADRES in
// AppWithOnboarding.js. Hier herhaald omdat dat bestand dit bestand inlaadt.
const KAART_ADRES = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
  + (process.env.REACT_APP_CARTO_KEY ? `?key=${process.env.REACT_APP_CARTO_KEY}` : '');

const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', canvas:'#ECEAE5',
  line:'#D9D5CD', line2:'#C4BFB5', head:'#F6F4F0',
  accent:'#1F5C6B', accent2:'#2D7F92', oker:'#C9A227', weekend:'#B5651D',
  ok:'#3F7D4E', warn:'#9C6420', red:'#B4442F',
};

// De kleur van elke status op de kaart van dit uur. Blauw, oker en rood zijn
// dezelfde als die van de laadpalen in het technisch fiche (PAAL_KLEUR in
// AppWithOnboarding.js). Tot 14 oktober 2026 was laden petrol, en dat was
// naast het groen van beschikbaar slecht te zien.
// Derde veld: hoe de status in een opsomming staat ("4 beschikbaar").
const STATUS = [
  ['laden',       'Aan het laden',   '#2E7BE6', 'aan het laden'],
  ['beschikbaar', 'Beschikbaar',     '#3A8D5C', 'beschikbaar'],
  ['geblokkeerd', 'Geblokkeerd',     '#D9A02B', 'geblokkeerd'],
  ['defect',      'Niet beschikbaar','#B4442F', 'niet beschikbaar'],
  ['onbekend',    'Status onbekend', '#5F6770', 'status onbekend'],
];
const KLEUR_GEEN = '#C9CDCF';
// De keuzes voor het overzicht per periode, en hoeveel er in beeld komen.
const PERIODES = [['week', 'Week'], ['maand', 'Maand'], ['kwartaal', 'Kwartaal'], ['jaar', 'Jaar']];
const PERIODE_MAX = { week: 8, maand: 12, kwartaal: 8, jaar: 100 };
// De kleurentabel per dag en uur: vijf vaste klassen (variant E, HL 15 oktober
// 2026). Geen meting is wit met grijze streepjes, zodat het op geen enkele
// kleur lijkt. Zelfde klassen als het blad Per dag en uur in de Excel.
const WEEKDAGEN = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const WARMTE_KLASSEN = [
  [0.10,     '#7FB3D5', 'minder dan 10 %'],
  [0.20,     '#D8ECC8', '10 tot 20 %'],
  [0.30,     '#F6D37A', '20 tot 30 %'],
  [0.40,     '#EF9447', '30 tot 40 %'],
  [Infinity, '#B83B2A', '40 % of meer'],
];
const WARMTE_GEEN = '#FFFFFF repeating-linear-gradient(45deg, #FFFFFF 0 4px, #C9C4BA 4px 6px)';
function warmteKleur(f) {
  return WARMTE_KLASSEN.find(([tot]) => f < tot)[1];
}
const VOLGORDE = [...STATUS.map(([k]) => k), 'geen'];
const KLEUR = { ...Object.fromEntries(STATUS.map(([k, , c]) => [k, c])), geen: KLEUR_GEEN };
const IN_ZIN = { ...Object.fromEntries(STATUS.map(([k, , , z]) => [k, z])),
                 geen: 'zonder meting' };

// Het icoon van één plek. Eén laadpunt: een gewone stip. Meer laadpunten: een
// ring met per status een deel naar verhouding, en het aantal in het midden.
function plekIcoon(telling) {
  const delen = VOLGORDE.filter(k => telling[k] > 0).map(k => [k, telling[k]]);
  const n = delen.reduce((a, [, c]) => a + c, 0);
  if (n <= 1) {
    const k = delen[0]?.[0] || 'geen';
    const r = k === 'defect' || k === 'geblokkeerd' ? 5 : 4;
    return { html: `<svg width="${2 * r + 2}" height="${2 * r + 2}" style="display:block">`
      + `<circle cx="${r + 1}" cy="${r + 1}" r="${r}" fill="${KLEUR[k]}" stroke="#fff" stroke-width="1"/></svg>`,
      maat: 2 * r + 2 };
  }
  const R = 11, m = R + 2;
  let stukken = '';
  if (delen.length === 1) {
    stukken = `<circle cx="${m}" cy="${m}" r="${R}" fill="${KLEUR[delen[0][0]]}"/>`;
  } else {
    let a = -Math.PI / 2;
    for (const [k, c] of delen) {
      const b = a + 2 * Math.PI * c / n;
      const x1 = m + R * Math.cos(a), y1 = m + R * Math.sin(a);
      const x2 = m + R * Math.cos(b), y2 = m + R * Math.sin(b);
      stukken += `<path d="M${m},${m} L${x1.toFixed(2)},${y1.toFixed(2)} A${R},${R} 0 ${b - a > Math.PI ? 1 : 0} 1 `
        + `${x2.toFixed(2)},${y2.toFixed(2)} Z" fill="${KLEUR[k]}" stroke="#fff" stroke-width="1"/>`;
      a = b;
    }
  }
  return { html: `<svg width="${2 * m}" height="${2 * m}" style="display:block">`
    + `<circle cx="${m}" cy="${m}" r="${R + 1.5}" fill="#fff"/>${stukken}`
    + `<circle cx="${m}" cy="${m}" r="${(R * 0.55).toFixed(1)}" fill="#fff"/>`
    + `<text x="${m}" y="${m + 4}" text-anchor="middle" font-size="11" font-weight="700" fill="#12161C" `
    + `font-family="inherit">${n}</text></svg>`, maat: 2 * m };
}

// Rood: de regels die HL op 14 oktober 2026 vastlegde.
const DREMPEL_DAGEN = 1;          // dagen boven de gekozen drempel
const FACTOR_DEFECT = 2;          // keer het stadsgemiddelde
const AFSTAND_MOW_M = 30;         // een MOW-punt telt als gemeten binnen deze afstand
// De keuzes voor de drempel. Gelijk aan DREMPEL_MIN, DREMPEL_MAX en
// DREMPEL_STAP in bezettingsmeting.js op de server.
const DREMPELS = [50, 55, 60, 65, 70, 75, 80];

const esc = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
const getal = (n) => Math.round(n).toLocaleString('nl-BE');
const pct0 = (f) => f == null ? '–' : `${Math.round(f * 100)} %`;
const pct1 = (f) => f == null ? '–' : `${(f * 100).toFixed(1).replace('.', ',')} %`;
const komma1 = (x) => x.toFixed(1).replace('.', ',');

function datumTekst(d) {
  return new Date(d).toLocaleDateString('nl-BE', { day:'numeric', month:'long', year:'numeric',
                                                   timeZone:'Europe/Brussels' });
}
function periodeTekst(van, tot) {
  if (!van || !tot) return '';
  const a = new Date(van), b = new Date(tot);
  const deel = (d, o) => d.toLocaleDateString('nl-BE', { ...o, timeZone:'Europe/Brussels' });
  if (deel(a, { month:'numeric', year:'numeric' }) === deel(b, { month:'numeric', year:'numeric' })) {
    return `${deel(a, { day:'numeric' })} tot en met ${datumTekst(b)}`;
  }
  return `${datumTekst(a)} tot en met ${datumTekst(b)}`;
}
function standTekst(uur) {
  if (!uur) return '';
  const d = new Date(uur);
  const tijd = d.toLocaleTimeString('nl-BE', { hour:'2-digit', minute:'2-digit', timeZone:'Europe/Brussels' });
  const dag = d.toLocaleDateString('nl-BE', { weekday:'long', day:'numeric', month:'long', year:'numeric',
                                              timeZone:'Europe/Brussels' });
  return `Stand van ${tijd}, ${dag}`;
}

function afstandM(lat1, lng1, lat2, lng2) {
  const x = (lng2 - lng1) * Math.cos((lat1 + lat2) / 2 * Math.PI / 180) * 111320;
  const y = (lat2 - lat1) * 111320;
  return Math.hypot(x, y);
}

// Een opsomming in gewone taal: "A, B en C".
function opsomming(delen) {
  if (delen.length <= 1) return delen.join('');
  return delen.slice(0, -1).join(', ') + ' en ' + delen[delen.length - 1];
}

export default function Bezetting({ actief, gemeente, wijkResults, existingPalen, sectorenGeo }) {
  const [laadsoort, setLaadsoort] = useState('gewoon');
  // Bezet is altijd "aan het laden". De schakelaar voor "aan het laden of
  // geblokkeerd" is op 14 oktober 2026 weggehaald: bijna geen exploitant geeft
  // de status geblokkeerd door, dus hij veranderde niets.
  const bezet = 'laden';
  // De drempel in procent. Leeg tot er iets gekozen is: dan geldt de drempel
  // die de server voor deze gemeente bewaard heeft.
  const [gekozen, setGekozen]     = useState(null);
  const [data, setData]           = useState(null);
  const [fout, setFout]           = useState('');
  const [drempelFout, setDrempelFout] = useState('');
  const [bezig, setBezig]         = useState(false);

  const gemId = gemeente?.id;

  // Andere gemeente: haar eigen bewaarde drempel weer laten gelden.
  useEffect(() => { setGekozen(null); setDrempelFout(''); }, [gemId]);

  useEffect(() => {
    if (!actief || !gemId) return;
    let weg = false;
    setBezig(true); setFout('');
    getBezetting(gemId, laadsoort, bezet, gekozen)
      .then(d => { if (!weg) setData(d); })
      .catch(e => { if (!weg) { setFout(e.message); setData(null); } })
      .finally(() => { if (!weg) setBezig(false); });
    return () => { weg = true; };
  }, [actief, gemId, laadsoort, bezet, gekozen]);

  const drempel = gekozen ?? data?.drempel ?? 65;

  // Een nieuwe drempel geldt meteen op het scherm en wordt bij de gemeente
  // bewaard. Lukt dat bewaren niet, dan staat dat in een melding; het scherm
  // rekent intussen wel met de gekozen drempel.
  const kiesDrempel = (v) => {
    setGekozen(v); setDrempelFout('');
    bewaarBezettingDrempel(gemId, v)
      .catch(e => {
        console.error('Drempel voor de bezetting niet bewaard:', e.message);
        setDrempelFout(e.message);
      });
  };

  // ── Per periode ──────────────────────────────────────────────────────
  const [periode, setPeriode]       = useState('week');
  const [excelBezig, setExcelBezig] = useState(false);
  const [excelFout, setExcelFout]   = useState('');

  // Alleen de wijken die in de berekening meetellen.
  const telMee = useMemo(() => new Set((wijkResults || []).map(r => r.wijk.id)), [wijkResults]);
  const wijken = useMemo(() => {
    const lijst = data?.wijken || [];
    if (!telMee.size) return lijst;
    return lijst.filter(w => telMee.has(w.id));
  }, [data, telMee]);

  const stadBez = data?.stad?.bezetting ?? null;
  const stadDef = data?.stad?.nietBeschikbaar ?? null;
  const heeftMeting = data?.stad?.bezetting != null || (data?.stand?.punten?.length || 0) > 0;

  // Per wijk: welke waarden rood worden.
  const rijen = useMemo(() => wijken.map(w => {
    const roodDagen = w.dagenBoven >= DREMPEL_DAGEN;
    const roodDefect = w.nietBeschikbaar != null && stadDef != null && stadDef > 0
      && w.nietBeschikbaar > FACTOR_DEFECT * stadDef;
    return { ...w, roodDagen, roodDefect };
  }), [wijken, stadDef]);

  // De laadpunten uit de MOW-database voor deze laadsoort, per plek. Laadpunten
  // op exact dezelfde plek horen bij één laadstation. Elk gemeten laadpunt hoort
  // bij de dichtstbijzijnde MOW-plek binnen 30 meter, of anders bij een eigen
  // plek. Grijs is per plek het aantal MOW-laadpunten min het aantal gemeten
  // laadpunten daar, zodat elk laadpunt één keer telt.
  const mow = useMemo(() => {
    const plekVan = new Map();
    let totaal = 0;
    for (const el of existingPalen || []) {
      const lat = el.lat ?? el.center?.lat, lng = el.lon ?? el.center?.lng ?? el.center?.lon;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const snel = ['snel', 'ultrasnel'].includes(el.tags?.mow_snelheid);
      if ((laadsoort === 'snel') !== snel) continue;
      totaal++;
      const k = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
      if (!plekVan.has(k)) plekVan.set(k, { lat, lng, mow: 0, telling: {}, exploitanten: new Set() });
      plekVan.get(k).mow++;
    }
    const mowPlekken = [...plekVan.values()];
    const eigen = new Map();
    for (const q of data?.stand?.punten || []) {
      if (!Number.isFinite(q.lat) || !Number.isFinite(q.lng)) continue;
      let beste = null, afstand = Infinity;
      for (const p of mowPlekken) {
        if (Math.abs(p.lat - q.lat) > 0.0005) continue;
        const d = afstandM(p.lat, p.lng, q.lat, q.lng);
        if (d <= AFSTAND_MOW_M && d < afstand) { beste = p; afstand = d; }
      }
      if (!beste) {
        const k = `${q.lat.toFixed(6)}_${q.lng.toFixed(6)}`;
        if (!eigen.has(k)) eigen.set(k, { lat: q.lat, lng: q.lng, mow: 0, telling: {}, exploitanten: new Set() });
        beste = eigen.get(k);
      }
      beste.telling[q.status] = (beste.telling[q.status] || 0) + 1;
      if (q.exploitant) beste.exploitanten.add(q.exploitant);
    }
    const plekken = [...mowPlekken, ...eigen.values()];
    let zonder = 0;
    for (const p of plekken) {
      const gemeten = Object.values(p.telling).reduce((a, c) => a + c, 0);
      p.telling.geen = Math.max(0, p.mow - gemeten);
      zonder += p.telling.geen;
    }
    return { totaal, zonder, plekken };
  }, [existingPalen, laadsoort, data]);

  // ── Signalen ─────────────────────────────────────────────────────────
  const signalen = useMemo(() => {
    if (!data?.stad) return null;
    // A. Boven de drempel
    const boven = rijen.filter(r => r.dagenBoven >= DREMPEL_DAGEN)
      .sort((a, b) => b.dagenBoven - a.dagenBoven);
    let tekstA;
    if (!boven.length) {
      tekstA = `Geen enkele wijk zat op een dag boven ${drempel} % bezetting.`;
    } else {
      const toon = boven.slice(0, 3).map((r, i) =>
        `${r.naam} (${r.dagenBoven}${i === 0 ? (r.dagenBoven === 1 ? ' dag' : ' dagen') : ''})`);
      if (boven.length > 3) toon.push(`${boven.length - 3} andere ${boven.length - 3 === 1 ? 'wijk' : 'wijken'}`);
      tekstA = `${opsomming(toon)} ${boven.length === 1 ? 'zat' : 'zaten'} op minstens één dag boven ${drempel} % `
        + 'bezetting.';
    }
    // B. Weinig gebruikt
    const laag = data.sectorenLaag?.aantal || 0;
    let tekstB = 'Geen enkele sector lag onder 10 % bezetting.';
    if (laag) {
      const perWijk = Object.entries(data.sectorenLaag.perWijk || {}).sort((a, b) => b[1] - a[1]);
      const top = perWijk[0];
      const naam = top ? (wijken.find(w => w.id === top[0])?.naam || top[0]) : null;
      tekstB = `In ${laag} ${laag === 1 ? 'sector' : 'sectoren'} lag de bezetting onder 10 %`
        + (top && laag > 1 ? `, waarvan ${top[1]} in ${naam}` : top ? `, in ${naam}` : '') + '.';
    }
    // C. Niet beschikbaar
    const hoogste = rijen.filter(r => r.nietBeschikbaar != null)
      .sort((a, b) => b.nietBeschikbaar - a.nietBeschikbaar)[0];
    const tekstC = 'Gemiddeld tijdsdeel dat laadpunten niet beschikbaar zijn.'
      + (hoogste ? ` Hoogste wijk: ${hoogste.naam} met ${pct1(hoogste.nietBeschikbaar)}.` : '');
    const aantal = (n, een, meer) => n ? `${n} ${n === 1 ? een : meer}` : 'Geen';
    return [
      { kop:`Boven de drempel van ${drempel} %`, wat: aantal(boven.length, 'wijk', 'wijken'), tekst: tekstA, let: boven.length > 0 },
      { kop:'Weinig gebruikt', wat: aantal(laag, 'sector', 'sectoren'), tekst: tekstB, let: false },
      { kop:'Niet beschikbaar', wat: pct1(stadDef), tekst: tekstC, let: false },
    ];
  }, [data, rijen, wijken, stadDef, drempel]);

  // ── Kaarten ──────────────────────────────────────────────────────────
  const wijkKaartRef = useRef(null), wijkKaart = useRef(null), wijkLaag = useRef(null);
  const standKaartRef = useRef(null), standKaart = useRef(null), standLaag = useRef(null);

  useEffect(() => {
    if (!actief) return;
    const maak = (ref, kaart, laag) => {
      if (!ref.current || kaart.current) return;
      kaart.current = L.map(ref.current, {
        center: [gemeente?.wijken?.[0]?.lat ?? 50.88, gemeente?.wijken?.[0]?.lng ?? 4.70],
        zoom: 12, zoomControl: false,
        // In kwart stappen, zodat de gemeente het beeld vult in plaats van
        // terug te springen naar een hele stap waarin ze maar half zo groot is.
        zoomSnap: 0.25, zoomDelta: 0.5,
      });
      L.control.zoom({ position: 'bottomleft' }).addTo(kaart.current);
      L.tileLayer(KAART_ADRES, { attribution: '© OpenStreetMap · CARTO', maxZoom: 19 }).addTo(kaart.current);
      laag.current = L.layerGroup().addTo(kaart.current);
    };
    maak(wijkKaartRef, wijkKaart, wijkLaag);
    maak(standKaartRef, standKaart, standLaag);
    // Na het tonen van een verborgen paneel weet Leaflet zijn afmetingen niet.
    const t = setTimeout(() => {
      wijkKaart.current?.invalidateSize();
      standKaart.current?.invalidateSize();
    }, 60);
    return () => clearTimeout(t);
  }, [actief, gemeente]);

  // Op welke sectorvormen de kaarten als laatste zijn ingezoomd. Bij een andere
  // gemeente komen er nieuwe vormen binnen, en dan wordt opnieuw ingezoomd.
  // Tot 16 oktober 2026 werd hier de gemeente onthouden. Bij het wisselen kwam
  // de nieuwe gemeente binnen terwijl de vormen van de vorige nog in beeld
  // stonden; de kaart zoomde dan op de vorige stad en markeerde de nieuwe als
  // klaar, waardoor hij niet meer meebewoog toen de juiste vormen binnenkwamen.
  const gepastVoor = useRef(null);

  // Kaart per wijk: alleen de naam en het percentage, geen kleurverschillen.
  useEffect(() => {
    if (!actief || !wijkKaart.current || !wijkLaag.current) return;
    try {
      // Het blok kan verborgen zijn geweest zolang er geen metingen waren.
      wijkKaart.current.invalidateSize();
      wijkLaag.current.clearLayers();
      let grens = null;
      if (sectorenGeo?.features?.length) {
        const vormen = L.geoJSON(sectorenGeo, { style: { color: N.accent, weight: 0.7, opacity: 0.45,
                                                         fillColor: '#FFFFFF', fillOpacity: 0.35 } });
        vormen.addTo(wijkLaag.current);
        grens = vormen.getBounds();
      }
      const bez = new Map(rijen.map(r => [r.id, r.bezetting]));
      for (const w of gemeente?.wijken || []) {
        if (!bez.has(w.id) || !Number.isFinite(w.lat) || !Number.isFinite(w.lng)) continue;
        const html = `<div style="transform:translate(-50%,-50%);text-align:center;white-space:nowrap;`
          + `font-family:inherit;color:${N.ink};text-shadow:0 0 3px #fff,0 0 3px #fff">`
          + `<div style="font-size:12.5px">${esc(w.naam)}</div>`
          + `<div style="font-size:16px;font-weight:700;color:${N.accent}">${esc(pct0(bez.get(w.id)))}</div></div>`;
        L.marker([w.lat, w.lng], { icon: L.divIcon({ className: '', html, iconSize: null }),
                                   interactive: false }).addTo(wijkLaag.current);
      }
      // Pas op de stad richten als de kaart echt in beeld staat. Een verborgen
      // kaart heeft geen afmeting, en dan zoomt Leaflet helemaal in.
      const zichtbaar = (wijkKaartRef.current?.offsetWidth || 0) > 0 && heeftMeting;
      if (grens?.isValid() && zichtbaar && gepastVoor.current !== sectorenGeo) {
        standKaart.current?.invalidateSize();
        wijkKaart.current.fitBounds(grens, { padding: [4, 4] });
        standKaart.current?.fitBounds(grens, { padding: [4, 4] });
        gepastVoor.current = sectorenGeo;
      }
    } catch (e) {
      console.error('Kaart per wijk op het tabblad Bezetting niet getekend:', e.message);
    }
  }, [actief, rijen, sectorenGeo, gemeente, gemId, heeftMeting]);

  // Kaart van dit uur: elk laadpunt in de kleur van zijn status.
  useEffect(() => {
    if (!actief || !standKaart.current || !standLaag.current) return;
    try {
      standKaart.current.invalidateSize();
      standLaag.current.clearLayers();
      const soort = laadsoort === 'snel' ? 'Snel laden' : 'Gewoon laden';
      for (const p of mow.plekken) {
        const n = VOLGORDE.reduce((a, k) => a + (p.telling[k] || 0), 0);
        if (!n) continue;
        const { html, maat } = plekIcoon(p.telling);
        const expl = [...p.exploitanten];
        const regelExpl = expl.length
          ? `${expl.length > 1 ? 'Exploitanten' : 'Exploitant'}: ${expl.map(esc).join(', ')}` : '';
        let tekst;
        if (n === 1) {
          const k = VOLGORDE.find(x => p.telling[x] > 0);
          tekst = k === 'geen' ? 'Geen meting'
            : `<b>${esc(STATUS.find(([x]) => x === k)[1])}</b><br>Exploitant: ${esc(expl[0] || 'onbekend')}<br>${soort}`;
        } else {
          tekst = `<b>${n} laadpunten op deze plek</b>`
            + VOLGORDE.filter(k => p.telling[k] > 0).map(k =>
                `<br><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${KLEUR[k]};`
                + `margin-right:6px"></span>${p.telling[k]} ${IN_ZIN[k]}`).join('')
            + (regelExpl ? `<br>${regelExpl}` : '') + `<br>${soort}`;
        }
        L.marker([p.lat, p.lng], { icon: L.divIcon({ className: '', html, iconSize: [maat, maat],
                                                     iconAnchor: [maat / 2, maat / 2] }),
                                   zIndexOffset: n > 1 ? 500 : 0 })
          .bindPopup(tekst)
          .addTo(standLaag.current);
      }
    } catch (e) {
      console.error('Kaart van dit uur op het tabblad Bezetting niet getekend:', e.message);
    }
  }, [actief, data, mow, laadsoort]);

  // De perioden in beeld: de laatste 8 weken, 12 maanden, 8 kwartalen of
  // alle jaren. Het Excel-bestand bevat ze allemaal.
  const perioden = useMemo(() => {
    const alle = data?.perPeriode?.[periode] || [];
    return alle.slice(-PERIODE_MAX[periode]);
  }, [data, periode]);
  const periodeVoet = useMemo(() => {
    const delen = [`Per periode het gemiddelde deel van de tijd dat een laadpunt bezet is, `
      + `${laadsoort === 'snel' ? 'snel' : 'gewoon'} laden.`];
    if (perioden.some(p => p.lopend)) delen.push('* Deze periode loopt nog.');
    const eerste = data?.perPeriode?.[periode]?.[0];
    const van = data?.meetperiode?.van;
    if (eerste && van && perioden[0]?.sleutel === eerste.sleutel) {
      const start = new Date(van).toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long',
                                                               timeZone: 'Europe/Brussels' });
      delen.push(`${eerste.kort[0].toUpperCase()}${eerste.kort.slice(1)} telt vanaf ${start}, de start van de meting.`);
    }
    delen.push('Een leeg vak: in die periode zijn in de wijk geen laadpunten gemeten.');
    return delen.join(' ');
  }, [data, periode, perioden, laadsoort]);

  const exporteer = () => {
    setExcelBezig(true); setExcelFout('');
    downloadBezettingExcel(gemId, { laadsoort, bezet, drempel: gekozen, wijkIds: rijen.map(r => r.id) })
      .catch(e => {
        console.error('Excel van de bezetting niet gemaakt:', e.message);
        setExcelFout(e.message);
      })
      .finally(() => setExcelBezig(false));
  };

  const dagUur = useMemo(() => new Map((data?.dagUur || []).map(v => [`${v.dag}|${v.uur}`, v.bezetting])
    .filter(([, f]) => f != null)), [data]);

  // ── Grafieken ────────────────────────────────────────────────────────
  const profiel = useMemo(() => {
    const uit = Array.from({ length: 24 }, (_, u) => ({ uur: u, werkdag: null, weekend: null }));
    for (const r of data?.dagprofiel?.werkdag || []) if (r.bezetting != null) uit[r.uur].werkdag = Math.round(r.bezetting * 1000) / 10;
    for (const r of data?.dagprofiel?.weekend || []) if (r.bezetting != null) uit[r.uur].weekend = Math.round(r.bezetting * 1000) / 10;
    return uit;
  }, [data]);
  const verloop = useMemo(() => (data?.perDag || []).filter(r => r.bezetting != null).map(r => ({
    datum: new Date(r.datum + 'T12:00:00Z').toLocaleDateString('nl-BE', { day:'numeric', month:'short' }),
    bezetting: Math.round(r.bezetting * 1000) / 10,
  })), [data]);

  // ── Weergave ─────────────────────────────────────────────────────────
  const s = stijl;
  const mp = data?.meetperiode;

  const knop = (aan, label, klik) => (
    <button key={label} style={s.schakel(aan)} onClick={klik}>{label}</button>
  );

  const rood = (aan, waarde, reden) => (
    <>
      <div style={aan ? s.roodGetal : undefined}>{waarde}</div>
      {aan && reden && <div style={s.reden}>{reden}</div>}
    </>
  );

  return (
    <div style={s.pagina}>
      {/* Eén band: de bezettingsgraad, wat erbij hoort en de schakelaars */}
      <div style={s.band}>
        <div>
          <div style={s.groot}>{pct0(stadBez)}</div>
          <div style={s.onderschrift}>bezettingsgraad, {laadsoort === 'snel' ? 'snel' : 'gewoon'} laden</div>
        </div>
        <div style={s.feiten}>
          {data?.vergelijk && (
            <div>Gemeenten van gelijke grootte: <b style={s.feitB}>{pct0(data.vergelijk.mediaan)}</b></div>
          )}
          {data?.stad && (
            <div><b style={s.feitB}>{getal(data.stad.gemeten)}</b>
              {mow.totaal ? ` van de ${getal(mow.totaal)} laadpunten uit de MOW-database gemeten` : ' laadpunten gemeten'}</div>
          )}
          {mp?.van && <div>{periodeTekst(mp.van, mp.tot)}, elk uur gemeten</div>}
        </div>
        <div style={s.schakelBlok}>
          <div style={s.schakelRij}>
            <span style={s.label}>Drempel</span>
            <select aria-label="Drempel" style={s.keuze} value={drempel}
                    onChange={e => kiesDrempel(Number(e.target.value))}>
              {DREMPELS.map(d => <option key={d} value={d}>{d} %</option>)}
            </select>
            <span style={{ ...s.label, marginLeft:8 }}>Laadsoort</span>
            <span style={s.schakelaars}>
              {knop(laadsoort === 'gewoon', 'Gewoon laden', () => setLaadsoort('gewoon'))}
              {knop(laadsoort === 'snel', 'Snel laden', () => setLaadsoort('snel'))}
            </span>
          </div>
          {bezig && <span style={{ fontSize:13, color:N.ink3 }}>Gegevens ophalen…</span>}
        </div>
      </div>

      {drempelFout && <div style={s.fout}>De drempel is niet bewaard: {drempelFout}</div>}
      {fout && <div style={s.fout}>De bezettingsgegevens konden niet worden opgehaald: {fout}</div>}
      {!fout && data && !heeftMeting && (
        <div style={s.leeg}>Voor deze gemeente zijn nog geen metingen.</div>
      )}

      {/* Signalen */}
      {signalen && heeftMeting && (
        <div style={s.signalen}>
          {signalen.map(g => (
            <div key={g.kop} style={s.signaal(g.let)}>
              <div style={s.label}>{g.kop}</div>
              <div style={s.signaalWat}>{g.wat}</div>
              <p style={s.signaalTekst}>{g.tekst}</p>
            </div>
          ))}
        </div>
      )}

      {/* Kaart en tabel per wijk */}
      <div style={{ ...s.rooster, display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <h2 style={s.h2}>Bezettingsgraad per wijk</h2>
          <div style={{ height:430, borderRadius:6, overflow:'hidden' }}>
            <div ref={wijkKaartRef} style={{ width:'100%', height:'100%' }} />
          </div>
          <div style={s.voet}>Per wijk het gemiddelde deel van de tijd dat een laadpunt bezet is, over alle
            uurmetingen in de meetperiode. Laadpunten die niet beschikbaar zijn of waarvan de status onbekend is, tellen
            niet mee.</div>
        </div>

        <div style={s.blok}>
          <h2 style={s.h2}>Per wijk</h2>
          <table style={s.tabel}>
            <thead><tr>
              <th style={s.th}>Wijk</th>
              <th style={s.thR}>Bezetting</th>
              <th style={s.thR}>Dagen boven {drempel} %</th>
              <th style={s.thR}>Gemeten laadpunten</th>
              <th style={s.thR}>Niet beschikbaar</th>
            </tr></thead>
            <tbody>
              {rijen.map(r => (
                <tr key={r.id}>
                  <td style={s.td}>{r.naam}</td>
                  <td style={s.tdR}>{pct0(r.bezetting)}</td>
                  <td style={s.tdR}>{rood(r.roodDagen, r.dagenBoven, null)}</td>
                  <td style={s.tdR}>{getal(r.gemeten)}</td>
                  <td style={s.tdR}>{rood(r.roodDefect, pct1(r.nietBeschikbaar),
                    r.roodDefect ? `${komma1(r.nietBeschikbaar / stadDef)} keer het stadsgemiddelde` : null)}</td>
                </tr>
              ))}
              <tr>
                <td style={s.tdSom}>{gemeente?.naam || 'Totaal'}</td>
                <td style={s.tdSomR}>{pct0(stadBez)}</td>
                <td style={s.tdSomR}>–</td>
                <td style={s.tdSomR}>{data?.stad ? getal(data.stad.gemeten) : '–'}</td>
                <td style={s.tdSomR}>{pct1(stadDef)}</td>
              </tr>
            </tbody>
          </table>
          <div style={s.voet}>
            Dagen boven {drempel} %: dagen waarop de wijk boven de gekozen drempel zat. Paal volgt Paal hanteert
            65 %. Niet beschikbaar: deel van de tijd dat de exploitant het laadpunt als buiten gebruik meldt.<br />
            Rood: dagen boven de drempel, 1 of meer. Niet beschikbaar, meer dan twee keer het stadsgemiddelde.
          </div>
        </div>
      </div>

      {/* Bezettingsgraad per periode */}
      <div style={{ ...s.rooster, gridTemplateColumns:'1fr', display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <div style={s.kopregel}>
            <h2 style={{ ...s.h2, margin:0 }}>Bezettingsgraad per periode</h2>
            <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <span style={s.schakelaars}>
                {PERIODES.map(([k, label]) => knop(periode === k, label, () => setPeriode(k)))}
              </span>
              <button style={s.knopExcel} disabled={excelBezig} onClick={exporteer}>
                {excelBezig ? 'Bezig…' : 'Exporteren naar Excel'}</button>
            </div>
          </div>
          {excelFout && <div style={{ ...s.fout, margin:'0 0 10px' }}>Het Excel-bestand kon niet worden gemaakt: {excelFout}</div>}
          <div style={{ overflowX:'auto' }}>
            <table style={s.tabel}>
              <thead><tr>
                <th style={s.th}>Wijk</th>
                {perioden.map(p => <th key={p.sleutel} style={s.thR}>{p.kort}{p.lopend ? '*' : ''}</th>)}
              </tr></thead>
              <tbody>
                <tr>
                  <td style={s.tdSom}>{gemeente?.naam || 'Totaal'}</td>
                  {perioden.map(p => <td key={p.sleutel} style={s.tdSomR}>{p.stad == null ? '' : pct0(p.stad)}</td>)}
                </tr>
                {rijen.map(r => (
                  <tr key={r.id}>
                    <td style={s.td}>{r.naam}</td>
                    {perioden.map(p => <td key={p.sleutel} style={s.tdR}>{p.wijken?.[r.id] == null ? '' : pct0(p.wijken[r.id])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={s.voet}>{periodeVoet}</div>
        </div>
      </div>

      {/* Stand van dit uur */}
      <div style={{ ...s.rooster, gridTemplateColumns:'1fr', display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <div style={s.kopregel}>
            <h2 style={{ ...s.h2, margin:0 }}>Stand van dit uur</h2>
            <span style={{ fontSize:15, color:N.ink2 }}>{standTekst(data?.stand?.uur)}</span>
          </div>
          <div style={s.standRooster}>
            <div style={{ height:430, borderRadius:6, overflow:'hidden' }}>
              <div ref={standKaartRef} style={{ width:'100%', height:'100%' }} />
            </div>
            <div>
              <table style={s.tabel}>
                <thead><tr>
                  <th style={s.th}>Status</th><th style={s.thR}>Laadpunten</th><th style={s.thR}>Deel</th>
                </tr></thead>
                <tbody>
                  {STATUS.map(([k, naam, kleur]) => {
                    const n = data?.stand?.perStatus?.[k] || 0;
                    const tot = data?.stand?.punten?.length || 0;
                    return (
                      <tr key={k}>
                        <td style={s.td}><span style={s.stip(kleur)} />{naam}</td>
                        <td style={s.tdR}>{getal(n)}</td>
                        <td style={s.tdR}>{tot ? `${Math.round(100 * n / tot)} %` : '–'}</td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td style={s.tdSom}>Gemeten laadpunten</td>
                    <td style={s.tdSomR}>{getal(data?.stand?.punten?.length || 0)}</td>
                    <td style={s.tdSomR}></td>
                  </tr>
                  <tr>
                    <td style={s.td}><span style={s.stip(KLEUR_GEEN)} />Geen meting</td>
                    <td style={s.tdR}></td>
                    <td style={s.tdR}></td>
                  </tr>
                </tbody>
              </table>
              <div style={s.voet}>Staan er meer laadpunten op één plek, dan staat het aantal in de stip en toont
                de ring hoeveel er in elke status zijn.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Exploitanten, stand van dit uur */}
      <div style={{ ...s.rooster, gridTemplateColumns:'1fr', display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <div style={s.kopregel}>
            <h2 style={{ ...s.h2, margin:0 }}>Exploitanten</h2>
            <span style={{ fontSize:15, color:N.ink2 }}>{standTekst(data?.stand?.uur)}</span>
          </div>
          <table style={s.tabel}>
            <thead><tr>
              <th style={s.th}>Exploitant</th><th style={s.thR}>Gemeten laadpunten</th>
              <th style={s.thR}>Aan het laden</th><th style={s.thR}>Niet beschikbaar</th><th style={s.thR}>Status onbekend</th>
            </tr></thead>
            <tbody>
              {(data?.stand?.exploitanten || []).map(e => (
                <tr key={e.naam}>
                  <td style={s.td}>{e.naam}</td>
                  <td style={s.tdR}>{getal(e.gemeten)}</td>
                  <td style={s.tdR}>{e.gemeten ? `${Math.round(100 * e.laden / e.gemeten)} %` : '–'}</td>
                  <td style={s.tdR}>{e.gemeten ? pct1(e.defect / e.gemeten) : '–'}</td>
                  <td style={s.tdR}>{e.gemeten ? pct1(e.onbekend / e.gemeten) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={s.voet}>Momentopname van het laatste hele uur, niet gemiddeld over de meetperiode.</div>
        </div>
      </div>

      {/* Dagprofiel en verloop */}
      <div style={{ ...s.rooster, display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <div style={s.kopregel}>
            <h2 style={{ ...s.h2, margin:0 }}>Bezetting per uur van de dag, heel {gemeente?.naam || 'de gemeente'}</h2>
            <span style={s.legenda}>
              <span><i style={s.lijn(N.accent)} />Werkdag</span>
              <span><i style={s.lijn(N.weekend)} />Weekend</span>
            </span>
          </div>
          <div style={{ height:230 }}>
            {actief && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={profiel} margin={{ top:8, right:12, left:0, bottom:0 }}>
                  <CartesianGrid stroke="#F0EEEA" vertical={false} />
                  <XAxis dataKey="uur" tick={{ fontSize:13 }} tickFormatter={u => `${u} u`} interval={5} />
                  <YAxis tick={{ fontSize:13 }} tickFormatter={v => `${v} %`} width={48} domain={[0, 'auto']} />
                  <Tooltip formatter={v => `${String(v).replace('.', ',')} %`} labelFormatter={u => `${u} uur`} />
                  <Line type="monotone" dataKey="werkdag" name="Werkdag" stroke={N.accent} strokeWidth={3} dot={false} connectNulls />
                  <Line type="monotone" dataKey="weekend" name="Weekend" stroke={N.weekend} strokeWidth={3} strokeDasharray="7 5" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={s.voet}>Gemiddeld deel van de laadpunten dat op elk uur van de dag bezet is, over alle dagen
            in de meetperiode.</div>
        </div>

        <div style={s.blok}>
          <h2 style={s.h2}>Verloop sinds de start van de meting</h2>
          <div style={{ height:230 }}>
            {actief && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={verloop} margin={{ top:8, right:12, left:0, bottom:0 }}>
                  <CartesianGrid stroke="#F0EEEA" vertical={false} />
                  <XAxis dataKey="datum" tick={{ fontSize:13 }} />
                  <YAxis tick={{ fontSize:13 }} tickFormatter={v => `${v} %`} width={48} domain={[0, 'auto']} />
                  <Tooltip formatter={v => `${String(v).replace('.', ',')} %`} />
                  <Line type="monotone" dataKey="bezetting" name="Bezetting" stroke={N.accent} strokeWidth={3} dot />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={s.voet}>Bezettingsgraad van de stad per dag. Na enkele maanden wordt dit per week getoond,
            zodat groei en het effect van nieuwe laadpunten zichtbaar worden.</div>
        </div>
      </div>

      {/* Per dag en uur, als kleurentabel. Alleen voor de hele gemeente: per
          uur bewaart de server alleen het totaal van de gemeente. */}
      <div style={{ ...s.rooster, gridTemplateColumns:'1fr', display: heeftMeting ? 'grid' : 'none' }}>
        <div style={s.blok}>
          <h2 style={s.h2}>Bezetting per dag en uur, heel {gemeente?.naam || 'de gemeente'}</h2>
          <div style={{ overflowX:'auto' }}>
            <table style={s.warmte}>
              <thead><tr>
                <th style={s.warmteDag} />
                {Array.from({ length: 24 }, (_, u) => <th key={u} style={s.warmteUur}>{u}</th>)}
              </tr></thead>
              <tbody>
                {WEEKDAGEN.map((dag, di) => (
                  <tr key={dag}>
                    <th style={s.warmteDag}>{dag}</th>
                    {Array.from({ length: 24 }, (_, u) => {
                      const f = dagUur.get(`${di + 1}|${u}`);
                      return <td key={u} style={f == null ? s.warmteGeen : s.warmteVak(warmteKleur(f))}
                                 title={f == null ? `${dag} ${u} uur: geen meting` : `${dag} ${u} uur: ${pct0(f)}`} />;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={s.warmteLegenda}>
            {WARMTE_KLASSEN.map(([, kleur, label]) => (
              <span key={label} style={s.warmteLegItem}>
                <span style={{ ...s.warmteVak(kleur), ...s.warmteLegVak }} />{label}</span>
            ))}
            <span style={{ ...s.warmteLegItem, marginLeft:8 }}>
              <span style={{ ...s.warmteGeen, ...s.warmteLegVak }} />Geen meting</span>
          </div>
          <div style={s.voet}>Gemiddeld deel van de laadpunten dat bezet is, per weekdag en uur, over de hele
            meetperiode.</div>
        </div>
      </div>

      <div style={s.onder}>
        <div style={s.bron}>De bezettingsgraad telt publieke en semipublieke laadpunten samen.</div>
      </div>
    </div>
  );
}

const stijl = {
  pagina:   { background:N.canvas, color:N.ink },
  label:    { fontSize:13, letterSpacing:'.08em', textTransform:'uppercase', color:N.ink2 },
  band:     { display:'flex', alignItems:'center', gap:28, background:N.paper, borderBottom:`1px solid ${N.line}`,
              padding:'14px 24px', flexWrap:'wrap' },
  groot:    { fontSize:38, fontWeight:700, color:N.accent, lineHeight:1 },
  onderschrift:{ fontSize:13, color:N.ink3, marginTop:4 },
  feiten:   { flex:1, minWidth:280, fontSize:15, color:N.ink2, lineHeight:1.6, borderLeft:`1px solid ${N.line}`,
              paddingLeft:24 },
  feitB:    { color:N.ink },
  schakelBlok:{ display:'flex', flexDirection:'column', gap:8, alignItems:'flex-end' },
  schakelRij:{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', justifyContent:'flex-end' },
  warmte:   { borderCollapse:'separate', borderSpacing:3, width:'100%', tableLayout:'fixed', minWidth:760 },
  warmteDag:{ fontSize:15, fontWeight:400, textAlign:'left', width:110, color:N.ink2, padding:'0 6px 0 0' },
  warmteUur:{ fontSize:13, fontWeight:400, color:N.ink3, textAlign:'center' },
  warmteVak:(kleur) => ({ height:30, borderRadius:3, background:kleur }),
  warmteLegenda:{ display:'flex', alignItems:'center', gap:10, marginTop:12, fontSize:13, color:N.ink2, flexWrap:'wrap' },
  warmteGeen:{ height:30, borderRadius:3, background:WARMTE_GEEN, boxShadow:'inset 0 0 0 1px #C9C4BA' },
  warmteLegItem:{ display:'inline-flex', alignItems:'center', gap:6 },
  warmteLegVak:{ display:'inline-block', width:16, height:14 },
  knopExcel:{ padding:'7px 14px', fontSize:15, border:`1px solid ${N.accent}`, borderRadius:6, background:'#fff',
              color:N.accent, cursor:'pointer', fontFamily:'inherit', fontWeight:600 },
  keuze:    { fontSize:15, padding:'5px 8px', border:`1px solid ${N.line}`, borderRadius:6, background:'#fff',
              fontFamily:'inherit', color:N.ink },
  schakelaars:{ display:'inline-flex', border:`1px solid ${N.line}`, borderRadius:6, overflow:'hidden', background:'#fff' },
  schakel:  (aan) => ({ padding:'7px 14px', fontSize:15, border:'none', cursor:'pointer', fontFamily:'inherit',
              background: aan ? N.accent : '#fff', color: aan ? '#fff' : N.ink2 }),
  fout:     { margin:'16px 24px 0', background:'#FBEEEB', border:'1px solid #E8C4BC', borderLeft:`3px solid ${N.red}`,
              borderRadius:9, padding:'11px 14px', fontSize:15, color:N.red },
  leeg:     { margin:'16px 24px 0', padding:'22px', textAlign:'center', color:N.ink3, fontSize:15,
              background:N.paper, border:`1px solid ${N.line}`, borderRadius:8 },
  signalen: { display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:12, padding:'16px 24px 0' },
  signaal:  (letOp) => ({ background:N.paper, border:`1px solid ${N.line}`,
              borderTop:`4px solid ${letOp ? N.oker : N.accent}`, borderRadius:8, padding:'12px 14px' }),
  signaalWat:{ fontSize:22, fontWeight:700, margin:'6px 0' },
  signaalTekst:{ fontSize:15, margin:0, lineHeight:1.4 },
  rooster:  { display:'grid', gridTemplateColumns:'1.15fr 1fr', gap:16, padding:'16px 24px 0' },
  standRooster:{ display:'grid', gridTemplateColumns:'1.6fr 1fr', gap:16, alignItems:'start' },
  blok:     { background:N.paper, border:`1px solid ${N.line}`, borderRadius:8, padding:'14px 16px', minWidth:0 },
  h2:       { fontSize:17, margin:'0 0 10px' },
  kopregel: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap:10, flexWrap:'wrap' },
  tabel:    { width:'100%', borderCollapse:'collapse' },
  th:       { fontSize:13, fontWeight:600, textAlign:'left', color:N.ink2, padding:'6px 8px',
              borderBottom:`1px solid ${N.line}`, verticalAlign:'bottom' },
  thR:      { fontSize:13, fontWeight:600, textAlign:'right', color:N.ink2, padding:'6px 8px',
              borderBottom:`1px solid ${N.line}`, verticalAlign:'bottom', width:96 },
  td:       { fontSize:15, padding:'7px 8px', borderBottom:'1px solid #F0EEEA' },
  tdR:      { fontSize:15, padding:'7px 8px', borderBottom:'1px solid #F0EEEA', textAlign:'right',
              whiteSpace:'nowrap', verticalAlign:'top' },
  tdSom:    { fontSize:15, padding:'7px 8px', fontWeight:700, borderTop:`2px solid ${N.line}` },
  tdSomR:   { fontSize:15, padding:'7px 8px', fontWeight:700, borderTop:`2px solid ${N.line}`, textAlign:'right' },
  roodGetal:{ color:N.red, fontWeight:600 },
  reden:    { fontSize:12.5, color:N.red, fontWeight:400, whiteSpace:'normal' },
  voet:     { fontSize:12.5, color:N.ink3, marginTop:8, lineHeight:1.45 },
  stip:     (kleur) => ({ display:'inline-block', width:11, height:11, borderRadius:'50%', background:kleur, marginRight:8 }),
  legenda:  { fontSize:13, color:N.ink2, display:'flex', gap:16 },
  lijn:     (kleur) => ({ display:'inline-block', width:18, height:3, background:kleur, verticalAlign:'middle', marginRight:6 }),
  onder:    { padding:'16px 24px 24px' },
  bron:     { fontSize:13, color:N.ink3, background:N.paper2, borderLeft:`3px solid ${N.oker}`, padding:'8px 10px', lineHeight:1.45 },
};
